import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faTimes,
  faSuitcase,
  faSuitcaseRolling,
} from "@fortawesome/free-solid-svg-icons";
import { format, parseISO, intervalToDuration } from "date-fns";

// Helper function to format duration string from API
const formatDurationString = (duration) => {
  if (!duration) return "";
  // If duration is in ISO format (PT10H30M)
  if (typeof duration === "string") {
    const hours = duration.match(/(\d+)H/)?.[1] || 0;
    const minutes = duration.match(/(\d+)M/)?.[1] || 0;
    return `${hours}h ${minutes}m`;
  }
  // If duration is an interval duration object
  return `${duration.hours || 0}h ${duration.minutes || 0}m`;
};

const StayDetailsModal = ({ isOpen, onClose, selectedOffer }) => {
  if (!isOpen) return null;

  // Update to use selectedOffer.data.slices directly
  const slices = selectedOffer?.data?.slices || [];
  console.log("Data available in modal:", selectedOffer);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Flight Details</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xl" />
            </button>
          </div>

          {slices.map((slice, sliceIndex) => (
            <div key={slice.id} className="mb-8">
              <h3 className="text-lg font-semibold mb-4">
                {sliceIndex === 0 ? "Outbound Flight" : "Return Flight"}
              </h3>

              {/* Flight segments timeline */}
              <div className="space-y-8">
                {slice.segments.map((segment, index) => (
                  <div key={segment.id} className="relative">
                    {/* Timeline connector */}
                    {index < slice.segments.length - 1 && (
                      <div className="absolute left-4 top-12 bottom-0 w-0.5 bg-gray-300 ml-[2px]" />
                    )}

                    <div className="flex items-start gap-4">
                      {/* Timeline dot */}
                      <div className="w-[10px] h-[10px] rounded-full bg-blue-600 mt-2" />

                      {/* Segment details */}
                      <div className="flex-1">
                        {/* Departure */}
                        <div className="mb-4">
                          <div className="flex items-baseline gap-3">
                            <span className="text-xl font-semibold">
                              {format(parseISO(segment.departing_at), "HH:mm")}
                            </span>
                            <span className="text-sm text-gray-500">
                              {format(
                                parseISO(segment.departing_at),
                                "EEE, d MMM"
                              )}
                            </span>
                          </div>
                          <p className="font-medium">{segment.origin.name}</p>
                        </div>

                        {/* Flight info */}
                        <div className="flex items-center gap-4 my-3 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            {segment.operating_carrier.logo_symbol_url && (
                              <img
                                src={segment.operating_carrier.logo_symbol_url}
                                alt={segment.operating_carrier.name}
                                className="h-6 w-6 object-contain"
                              />
                            )}
                            <span>{segment.operating_carrier.name}</span>
                          </div>
                          <span>•</span>
                          <span>
                            Flight {segment.operating_carrier_flight_number}
                          </span>
                          <span>•</span>
                          <span>{formatDurationString(segment.duration)}</span>
                        </div>

                        {/* Arrival */}
                        <div className="mt-4">
                          <div className="flex items-baseline gap-3">
                            <span className="text-xl font-semibold">
                              {format(parseISO(segment.arriving_at), "HH:mm")}
                            </span>
                            <span className="text-sm text-gray-500">
                              {format(
                                parseISO(segment.arriving_at),
                                "EEE, d MMM"
                              )}
                            </span>
                          </div>
                          <p className="font-medium">
                            {segment.destination.name}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Connection information */}
                    {index < slice.segments.length - 1 && (
                      <div className="ml-8 mt-4 p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FontAwesomeIcon icon={faClock} />
                          <span>
                            Connection in {segment.destination.city_name}:{" "}
                            {formatDurationString(
                              intervalToDuration({
                                start: parseISO(segment.arriving_at),
                                end: parseISO(
                                  slice.segments[index + 1].departing_at
                                ),
                              })
                            )}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Total journey information */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <FontAwesomeIcon icon={faClock} />
                  <span>
                    Total duration: {formatDurationString(slice.duration)}
                  </span>
                  {slice.segments.length > 1 && (
                    <>
                      <span>•</span>
                      <span>
                        {slice.segments.length - 1} stop
                        {slice.segments.length - 1 > 1 ? "s" : ""}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 border-t border-gray-200 p-6">
          <h3 className="text-lg font-semibold mb-4">Baggage Allowance</h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <FontAwesomeIcon
                    icon={faSuitcase}
                    className="text-gray-600 text-xl"
                  />
                </div>
                <div>
                  <p className="font-medium">Carry-on Baggage</p>
                  <p className="text-sm text-gray-600">
                    {selectedOffer.data.slices[0].segments[0].passengers[0].baggages
                      .filter((bag) => bag.type === "carry_on")
                      .map(
                        (bag) =>
                          `${bag.quantity} bag${bag.quantity > 1 ? "s" : ""}`
                      )
                      .join(", ")}{" "}
                    included
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <FontAwesomeIcon
                    icon={faSuitcaseRolling}
                    className="text-gray-600 text-xl"
                  />
                </div>
                <div>
                  <p className="font-medium">Checked Baggage</p>
                  <p className="text-sm text-gray-600">
                    {selectedOffer.data.slices[0].segments[0].passengers[0].baggages
                      .filter((bag) => bag.type === "checked")
                      .map(
                        (bag) =>
                          `${bag.quantity} bag${bag.quantity > 1 ? "s" : ""}`
                      )
                      .join(", ") || "Not included"}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 text-sm text-gray-500">
              <p>* Baggage allowance is per passenger</p>
              <p>* Additional baggage can be purchased separately</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StayDetailsModal;
